import { useId, useLayoutEffect, useRef, useState } from "preact/hooks";
import { dropWrites, moveBy, placeNear, sameMembers } from "./sortable";

const EDGE = 40;
const SCROLL = 12;

// Reordering by a grip: a pointer (mouse or touch) drag, or ArrowUp and ArrowDown on the focused grip. Rows are drawn in
// `order`, which follows the pointer while a drag is on and is `ids` otherwise; `onReorder` gets the finished order, the id moved and, for a drag, the row it was dropped on.
// Text selection is off for the length of a drag. `announce` words a keyboard move for a screen reader ("name, 2 of 3"), given the `over` target a `step` named;
// the hook returns it as `announcement` for the caller to render in a polite live region. A drag ends without a write when
// `ids` changed under it (another session's reorder), and a drop that leaves the order as it was is still one for `changed`.
// `accepts` says whether the dragged id may be put where a target stands (a row, or a `drop` zone, which is what a group with no row
// is dropped on); a drag over a target it refuses leaves the preview as it was. `step` replaces the one-place key move.
export function useSortable({
	ids,
	onReorder,
	changed,
	announce,
	accepts,
	step = (list, id, delta) => {
		const next = moveBy(list, id, delta);
		return next && { ids: next };
	},
}: {
	ids: string[];
	onReorder: (ids: string[], moved: string, over?: string) => void;
	changed?: (ids: string[], moved: string, over?: string) => boolean;
	announce?: (id: string, position: number, total: number, over?: string) => string;
	accepts?: (moved: string, target: string) => boolean;
	step?: (ids: string[], moved: string, delta: number) => { ids: string[]; over?: string } | null;
}) {
	const group = useId();
	const [preview, setPreview] = useState<string[] | null>(null);
	const [announcement, setAnnouncement] = useState("");
	const [dragging, setDragging] = useState<string | null>(null);
	const [over, setOver] = useState<string | null>(null);
	const refocus = useRef<string | null>(null);
	const latest = useRef({ ids, onReorder, changed, announce, accepts, step });
	latest.current = { ids, onReorder, changed, announce, accepts, step };
	const order = preview && sameMembers(preview, ids) ? preview : ids;

	// Moving a focused row in the DOM drops its focus, whoever moved it (a key here, or another session's reorder arriving), so the
	// grip focused when a render began is focused again after it. Left on the body, the next arrow would reach the page's shortcuts.
	const active = document.activeElement;
	if (active instanceof HTMLElement && active.dataset.grip !== undefined) refocus.current = active.dataset.grip;
	useLayoutEffect(() => {
		if (refocus.current === null) return;
		document.querySelector<HTMLElement>(`[data-grip="${CSS.escape(refocus.current)}"]`)?.focus();
		refocus.current = null;
	});

	const stop = useRef<(() => void) | null>(null);
	// Layout effects, so a release before passive effects have run still finds the drag gone.
	useLayoutEffect(() => () => stop.current?.(), []);
	const key = ids.join();
	useLayoutEffect(() => stop.current?.(), [key]);

	const startDrag = (id: string, event: PointerEvent) => {
		if (event.button > 0 || stop.current) return;
		event.preventDefault();
		const pointer = event.pointerId;
		const start = latest.current.ids;
		let current = start;
		let target: string | undefined;
		const before = document.body.style.userSelect;
		document.body.style.userSelect = "none";
		setDragging(id);
		setPreview(current);

		const move = (moved: PointerEvent) => {
			if (moved.pointerId !== pointer) return;
			if (moved.clientY > window.innerHeight - EDGE) window.scrollBy(0, SCROLL);
			else if (moved.clientY < EDGE) window.scrollBy(0, -SCROLL);
			const row = document.elementFromPoint(moved.clientX, moved.clientY)?.closest<HTMLElement>("[data-sortable-row]");
			if (!row || row.dataset.sortableGroup !== group || !row.dataset.sortableRow) return;
			if (row.dataset.sortableRow === id || latest.current.accepts?.(id, row.dataset.sortableRow) === false) return;
			target = row.dataset.sortableRow;
			setOver(target);
			const box = row.getBoundingClientRect();
			const next = placeNear(current, id, row.dataset.sortableRow, moved.clientY > box.top + box.height / 2);
			if (next !== current) {
				current = next;
				setPreview(next);
			}
		};
		const end = (ended: PointerEvent) => {
			if (ended.pointerId !== pointer) return;
			stop.current?.();
			if (ended.type === "pointercancel") return;
			if (dropWrites(start, latest.current.ids, current, () => latest.current.changed?.(current, id, target) ?? false))
				latest.current.onReorder(current, id, target);
		};
		const release = () => {
			document.removeEventListener("pointermove", move);
			document.removeEventListener("pointerup", end);
			document.removeEventListener("pointercancel", end);
			document.body.style.userSelect = before;
			setDragging(null);
			setOver(null);
			setPreview(null);
			stop.current = null;
		};
		stop.current = release;
		document.addEventListener("pointermove", move);
		document.addEventListener("pointerup", end);
		document.addEventListener("pointercancel", end);
	};

	const keyMove = (id: string, event: KeyboardEvent) => {
		if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
		event.preventDefault();
		const next = latest.current.step(latest.current.ids, id, event.key === "ArrowUp" ? -1 : 1);
		if (!next) return;
		refocus.current = id;
		latest.current.onReorder(next.ids, id, next.over);
		setAnnouncement(latest.current.announce?.(id, next.ids.indexOf(id) + 1, next.ids.length, next.over) ?? "");
	};

	return {
		order,
		dragging,
		// The row under the pointer during a drag.
		over,
		announcement,
		// Spread on the row: what the drag finds under the pointer.
		row: (id: string) => ({ "data-sortable-row": id, "data-sortable-group": group }),
		// Spread on an empty group's drop zone: what a drag finds under the pointer when the group has no row.
		drop: (target: string) => ({ "data-sortable-row": target, "data-sortable-group": group }),
		// Spread on the grip button.
		grip: (id: string) => ({
			"data-grip": id,
			onPointerDown: (event: PointerEvent) => startDrag(id, event),
			onKeyDown: (event: KeyboardEvent) => keyMove(id, event),
		}),
	};
}
